/**
 * SuggestionsPage — admin catalog-suggestions review queue (PIM Wave P3
 * Task 4). Sellers propose a new category/field/option (name + note); an
 * admin reviews the queue oldest-first and approves or declines.
 *
 * Chrome mirrors the sibling `product-review/ProductReviewPage.tsx`
 * (hand-rolled `aria-pressed` status chips, `useInfiniteQuery` w/ 30s
 * refetch, oldest-first, "Load more") layered onto the P0 kit's
 * `AdminPageHeader` + `AdminStates`. Unlike Task 3, the backend DOES ship a
 * counts endpoint (`GET /admin/catalog/suggestions/counts`) so a
 * `CountsBar` is included here, mirroring `seller-applications/
 * QueueListPage.tsx`'s `CountsBar` shape (grid of tiles, `Skeleton` while
 * loading, `text-2xl font-bold text-[var(--g-primary)]` count + label).
 */
import { useCallback, useMemo, useState, type ReactElement } from "react";
import { useTranslation } from "react-i18next";

import { GPill, Skeleton } from "@garden/ui";
import { ADMIN_NS, registerAdminI18n } from "@/features/admin/api";
import { AdminPageHeader, AdminStates } from "@/features/admin/_shared";
import {
  suggestionKindTone,
  suggestionStatusTone,
  type SuggestionResponse,
} from "@/features/admin/suggestions/api";
import { useSuggestionCounts, useSuggestions } from "@/features/admin/suggestions/hooks";
import { SuggestionDrawer } from "@/features/admin/suggestions/components/SuggestionDrawer";

const STATUS_BUCKETS = ["pending", "approved", "declined"] as const;
type StatusBucket = (typeof STATUS_BUCKETS)[number];

const FILTER_LABEL_KEY: Record<StatusBucket, string> = {
  pending: "pim.suggestions.filters.pending",
  approved: "pim.suggestions.filters.approved",
  declined: "pim.suggestions.filters.declined",
};

function formatDate(iso: string): string {
  try {
    return new Intl.DateTimeFormat("ru-RU", {
      day: "2-digit",
      month: "2-digit",
      year: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    }).format(new Date(iso));
  } catch {
    return iso;
  }
}

function CountsBar(): ReactElement {
  const { t } = useTranslation(ADMIN_NS);
  const { data: counts, isLoading } = useSuggestionCounts();

  const tiles: Array<{ key: string; label: string; value: number }> = [
    { key: "pending", label: t("pim.suggestions.counts.pending"), value: counts?.pending ?? 0 },
    { key: "approved", label: t("pim.suggestions.counts.approved"), value: counts?.approved ?? 0 },
    { key: "declined", label: t("pim.suggestions.counts.declined"), value: counts?.declined ?? 0 },
  ];

  return (
    <div
      data-testid="suggestions-counts-bar"
      className="grid grid-cols-3 gap-2"
      aria-label={t("pim.suggestions.counts.ariaLabel")}
    >
      {tiles.map(({ key, label, value }) => (
        <div
          key={key}
          className="flex flex-col items-center rounded-[var(--g-radius-md)] bg-[var(--g-elevated)] px-3 py-2 text-center"
        >
          {isLoading ? (
            <Skeleton className="h-7 w-10" />
          ) : (
            <span className="text-2xl font-bold text-[var(--g-primary)]">{value}</span>
          )}
          <span className="mt-0.5 text-xs text-[var(--g-secondary)]">{label}</span>
        </div>
      ))}
    </div>
  );
}

interface SuggestionRowProps {
  item: SuggestionResponse;
  onClick: (item: SuggestionResponse) => void;
}

function SuggestionRow({ item, onClick }: SuggestionRowProps): ReactElement {
  const { t } = useTranslation(ADMIN_NS);
  const activate = (): void => onClick(item);
  return (
    <tr
      data-testid={`suggestion-row-${item.id}`}
      onClick={activate}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          activate();
        }
      }}
      tabIndex={0}
      role="button"
      aria-label={item.payload.name}
      className="cursor-pointer border-b border-[var(--g-hairline)] hover:bg-[var(--g-elevated)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[var(--g-primary)]"
    >
      <td className="px-3 py-3">
        <GPill tone={suggestionKindTone(item.kind)} size="sm">
          {t(`pim.suggestions.kind.${item.kind}`)}
        </GPill>
      </td>
      <td className="min-h-[44px] px-3 py-3 text-sm font-medium">{item.payload.name}</td>
      <td className="px-3 py-3 text-sm text-[var(--g-secondary)]">
        {item.seller_id.slice(0, 8)}
      </td>
      <td className="px-3 py-3">
        <GPill tone={suggestionStatusTone(item.status)} size="sm">
          {t(`pim.suggestions.status.${item.status}`, { defaultValue: item.status })}
        </GPill>
      </td>
      <td className="px-3 py-3 text-sm text-[var(--g-secondary)]">{formatDate(item.created_at)}</td>
    </tr>
  );
}

function SuggestionCard({ item, onClick }: SuggestionRowProps): ReactElement {
  const { t } = useTranslation(ADMIN_NS);
  const activate = (): void => onClick(item);
  return (
    <div
      data-testid={`suggestion-card-${item.id}`}
      onClick={activate}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          activate();
        }
      }}
      tabIndex={0}
      role="button"
      aria-label={item.payload.name}
      className="cursor-pointer rounded-[var(--g-radius)] border border-[var(--g-hairline)] bg-[var(--g-surface)] p-4 shadow-[var(--g-shadow-sm)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--g-primary)]"
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <GPill tone={suggestionKindTone(item.kind)} size="sm">
              {t(`pim.suggestions.kind.${item.kind}`)}
            </GPill>
          </div>
          <div className="mt-1 truncate font-medium">{item.payload.name}</div>
          <div className="mt-0.5 text-sm text-[var(--g-secondary)]">
            {item.seller_id.slice(0, 8)} · {formatDate(item.created_at)}
          </div>
        </div>
        <GPill tone={suggestionStatusTone(item.status)} size="sm">
          {t(`pim.suggestions.status.${item.status}`, { defaultValue: item.status })}
        </GPill>
      </div>
    </div>
  );
}

export function SuggestionsPage(): ReactElement {
  registerAdminI18n();
  const { t } = useTranslation(ADMIN_NS);

  const [status, setStatus] = useState<StatusBucket>("pending");
  const [selected, setSelected] = useState<SuggestionResponse | null>(null);

  const { data, isLoading, isError, isFetchingNextPage, hasNextPage, fetchNextPage } =
    useSuggestions(status);

  const handleSelect = useCallback((item: SuggestionResponse) => {
    setSelected(item);
  }, []);

  const items: SuggestionResponse[] = useMemo(
    () => data?.pages.flatMap((p) => p.suggestions) ?? [],
    [data],
  );

  return (
    <div data-testid="suggestions-page" className="flex flex-col gap-4">
      <AdminPageHeader
        title={t("pim.suggestions.title")}
        subtitle={t("pim.suggestions.subtitle")}
      />

      <CountsBar />

      <div
        data-testid="suggestions-filters"
        className="flex flex-wrap items-center gap-2"
      >
        {STATUS_BUCKETS.map((bucket) => (
          <button
            key={bucket}
            type="button"
            onClick={() => setStatus(bucket)}
            aria-pressed={status === bucket}
            className={[
              "min-h-[44px] rounded-full px-3 py-1 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--g-primary)]",
              status === bucket
                ? "bg-[var(--g-primary)] text-white"
                : "bg-[var(--g-elevated)] text-[var(--g-secondary)] hover:bg-[var(--g-hairline)]",
            ].join(" ")}
          >
            {t(FILTER_LABEL_KEY[bucket])}
          </button>
        ))}
      </div>

      <AdminStates
        isLoading={isLoading}
        isError={isError}
        isEmpty={items.length === 0}
        loadingLabel={t("pim.suggestions.title")}
        emptyLabel={t("pim.suggestions.empty")}
        errorLabel={t("pim.suggestions.error")}
        testid="suggestions"
      >
        <>
          <div className="hidden sm:block overflow-x-auto rounded-[var(--g-radius-md)] border border-[var(--g-hairline)]">
            <table
              data-testid="suggestions-table"
              className="w-full min-w-[640px] border-collapse bg-[var(--g-surface)] text-sm"
            >
              <thead className="bg-[var(--g-elevated)]">
                <tr>
                  <th
                    scope="col"
                    className="px-3 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-[var(--g-secondary)]"
                  >
                    {t("pim.suggestions.columns.kind")}
                  </th>
                  <th
                    scope="col"
                    className="px-3 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-[var(--g-secondary)]"
                  >
                    {t("pim.suggestions.columns.name")}
                  </th>
                  <th
                    scope="col"
                    className="px-3 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-[var(--g-secondary)]"
                  >
                    {t("pim.suggestions.columns.seller")}
                  </th>
                  <th
                    scope="col"
                    className="px-3 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-[var(--g-secondary)]"
                  >
                    {t("pim.suggestions.columns.status")}
                  </th>
                  <th
                    scope="col"
                    className="px-3 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-[var(--g-secondary)]"
                  >
                    {t("pim.suggestions.columns.createdAt")}
                  </th>
                </tr>
              </thead>
              <tbody>
                {items.map((item) => (
                  <SuggestionRow key={item.id} item={item} onClick={handleSelect} />
                ))}
              </tbody>
            </table>
          </div>

          <div className="flex flex-col gap-2 sm:hidden" data-testid="suggestions-cards">
            {items.map((item) => (
              <SuggestionCard key={item.id} item={item} onClick={handleSelect} />
            ))}
          </div>

          {hasNextPage ? (
            <button
              type="button"
              onClick={() => void fetchNextPage()}
              disabled={isFetchingNextPage}
              className="mx-auto min-h-[44px] rounded-full bg-[var(--g-elevated)] px-6 py-2 text-sm font-medium text-[var(--g-secondary)] hover:bg-[var(--g-hairline)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--g-primary)] disabled:opacity-50"
            >
              {isFetchingNextPage ? t("common.refreshing") : t("common.loadMore")}
            </button>
          ) : null}
        </>
      </AdminStates>

      <SuggestionDrawer
        suggestion={selected}
        open={selected !== null}
        onClose={() => setSelected(null)}
      />
    </div>
  );
}

export default SuggestionsPage;
